import {
  abcAnalysis, materialReconciliation, materialRequirementPlan, poExpediting, stockAuditStatus, stockLedger,
  vendorScorecard,
} from './supply-chain.js'
import { defineReport, rc, rf, type ReportDef } from './types.js'

/* ================= Management ================= */

export const projectFinancials = defineReport({
  key: 'project_financials', route: 'project_management_dashboard', group: 'Management',
  label: 'Financials — ongoing projects',
  blurb: "A bird's eye view of every project's value, billing and cost.",
  filters: [rf.customer(), rf.jobType()],
  columns: [
    rc.text('customerName', 'Customer', 220), rc.doc('projectNo', 'Project'), rc.text('projectName', 'Name'),
    rc.money('projectValue', 'Project value'), rc.money('billed', 'Billed'),
    rc.money('unbilled', 'Unbilled'), rc.money('cost', 'Cost incurred'),
    rc.signed('margin', 'Margin'), rc.pct('marginPct', 'Margin %'),
  ],
  drill: { by: 'project', key: 'id', label: 'Documents on this project' },} as const)

export const completedProfitability = defineReport({
  key: 'completed_profitability', route: 'completed_projects_profit', group: 'Management',
  label: 'Profitability — completed projects',
  blurb: 'Profit on projects that are finished, so the estimate can be checked against reality.',
  // 'Project manager' is the second filter docs/10 names on this report, and
  // the master has carried the column all along.
  filters: [
    rf.period('Completed between'), rf.customer(),
    { key: 'employeeId', label: 'Project manager', control: 'lookup', lookup: { entity: 'employee' } },
  ],
  columns: [
    rc.doc('projectNo', 'Project'), rc.text('projectName', 'Name'),
    rc.date('completedOn', 'Completed'),
    rc.money('billed', 'Billed'), rc.money('cost', 'Cost'),
    rc.signed('margin', 'Profit'), rc.pct('marginPct', 'Margin %'),
  ],
  drill: { by: 'project', key: 'id', label: 'Documents on this project' },} as const)

export const approvalTracker = defineReport({
  key: 'approval_tracker', route: 'doc_approval_tracker', group: 'Management',
  label: 'Document approval tracker',
  blurb: 'Everything waiting for approval, who it is with, and how long it has been there.',
  filters: [
    { key: 'documentStatus', label: 'Document status', control: 'select', required: true,
      options: [{ value: 'IN_REVIEW', label: 'In review' }, { value: 'DRAFT', label: 'Draft' },
                { value: 'APPROVED', label: 'Approved' }, { value: 'CANCELLED', label: 'Cancelled' }] },
    { key: 'docType', label: 'Document type', control: 'select', options: [] },
    rf.period('Document date'), rf.project(),
  ],
  columns: [
    rc.doc(), rc.text('docTypeLabel', 'Type', 190), rc.date('documentDate', 'Date'),
    rc.text('currentStage', 'Waiting at'), rc.text('withUser', 'With', 170),
    { key: 'waitingFor', label: 'Waiting for', type: 'duration', width: 140, align: 'right' },
    rc.text('projectName', 'Project'),
  ],
} as const)

/* ================= Project ================= */

export const profitAnalysis = defineReport({
  key: 'profit_analysis', route: 'profit_analysis', group: 'Project',
  label: 'Profitability analysis',
  blurb: 'Estimated against projected profit for one project.',
  filters: [rf.project(true), rf.includeSub()],
  caveat: 'Rate-only BOQ lines may have incurred cost, so the projected margin can read lower than the actual.',
  columns: [
    rc.text('head', 'Head', 260), rc.money('estimated', 'Estimated'), rc.money('actual', 'Actual'),
    rc.signed('variance', 'Variance'), rc.pct('variancePct', 'Variance %'),
  ],
  hasSummaryDetail: true,
} as const)

export const costVariance = defineReport({
  key: 'cost_variance', route: 'cost_variance', group: 'Project',
  label: 'Cost variance analysis',
  blurb: 'Where a project is spending more than it estimated, broken down by cost type.',
  filters: [rf.project(true), rf.jobType(), rf.includeSub()],
  columns: [
    rc.text('costType', 'Cost type', 200), rc.text('itemName', 'Item'),
    rc.money('estimated', 'Estimated'), rc.money('incurred', 'Incurred'),
    rc.signed('variance', 'Variance'), rc.pct('variancePct', 'Variance %'),
  ],
  hasSummaryDetail: true,
  // The rows are grouped by item NAME, because an estimate and an invoice can
  // name the same material through different item records. The drill matches
  // the same way, so what opens is what was totalled.
  drill: {
    by: 'item', key: 'itemName',
    docTypes: ['purchase_invoice', 'purchase_order', 'other_cost', 'expense_report'],
    label: 'What was spent on this',
  },} as const)

export const regulariseBoq = defineReport({
  key: 'regularise_boq', route: 'regularise_boq', group: 'Project',
  label: 'Regularise BOQ',
  blurb: 'BOQ lines where what was ordered no longer matches what was designed.',
  filters: [rf.project(true)],
  columns: [
    rc.text('boqRef', 'BOQ no', 130), rc.text('itemName', 'Description'),
    rc.qty('orderQty', 'Order qty'), rc.qty('designQty', 'Design qty'),
    rc.qty('variance', 'Variance'), rc.money('valueImpact', 'Value impact'),
  ],
} as const)

export const billingReconciliation = defineReport({
  key: 'billing_reconciliation', route: 'billing_reconcile', group: 'Project',
  label: 'Billing reconciliation',
  blurb: 'What has been billed against what the BOQ allows, line by line.',
  filters: [rf.project(true), rf.includeSub()],
  columns: [
    rc.text('boqRef', 'BOQ no', 130), rc.text('itemName', 'Description'),
    rc.text('billingTerm', 'Billing term', 200),
    // How the line's progress was measured, and what it came to. Without both,
    // "why may I only bill 60 of the 100 I ordered" has no answer on screen.
    rc.text('progressMethod', 'Measured as', 140), rc.pct('progressPct', 'Progress %'),
    rc.qty('orderQty', 'Billable qty'), rc.qty('billedQty', 'Billed qty'),
    rc.qty('balanceQty', 'Balance'), rc.money('billedValue', 'Billed value'),
    rc.money('balanceValue', 'Left to bill'),
  ],
} as const)

export const projectExpenses = defineReport({
  key: 'project_expenses', route: 'project_expenses_itemised', group: 'Project',
  label: 'Project expenses itemised',
  blurb: 'Every cost booked to a project, itemised and dated.',
  filters: [rf.project(true), rf.period('Period', false)],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('docTypeLabel', 'Type', 180),
    rc.text('itemName', 'Item'), rc.text('supplierName', 'Paid to', 200),
    rc.qty('quantity', 'Qty'), rc.money('amount', 'Amount'),
  ],
  drill: { by: 'document', key: 'id', label: 'The document behind this' },} as const)

/* ================= Operation ================= */

export const materialPendingRequisition = defineReport({
  key: 'material_pending_requisition', route: 'mat_pend_mr', group: 'Operation',
  label: 'Material pending for requisition',
  blurb: 'Estimated material that nobody has requested yet.',
  filters: [rf.project(true), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.text('boqRef', 'BOQ no', 130),
    rc.qty('estimatedQty', 'Estimated'), rc.qty('requestedQty', 'Requested'),
    rc.qty('pendingQty', 'Still to request'),
  ],
} as const)

export const labourPendingSco = defineReport({
  key: 'labour_pending_sco', route: 'lab_pend_sco', group: 'Operation',
  label: 'Labour pending for subcontract',
  blurb: 'Estimated installation work not yet placed on a subcontract order.',
  filters: [rf.project(true)],
  columns: [
    rc.text('itemName', 'Work'), rc.text('boqRef', 'BOQ no', 130),
    rc.qty('estimatedQty', 'Estimated'), rc.qty('orderedQty', 'Ordered'),
    rc.qty('pendingQty', 'Still to order'), rc.money('pendingValue', 'Value'),
  ],
} as const)

export const materialRequestExecution = defineReport({
  key: 'material_request_execution', route: 'mat_req_exec_status', group: 'Operation',
  label: 'Material request execution',
  blurb: 'What has happened to each material request since it was raised.',
  filters: [rf.project(), rf.period('Requested between', false), rf.status()],
  columns: [
    rc.doc(), rc.date('documentDate', 'Raised'), rc.text('itemName', 'Material'),
    rc.qty('requestedQty', 'Requested'), rc.qty('orderedQty', 'Ordered'),
    rc.qty('receivedQty', 'Received'), rc.qty('pendingQty', 'Open'),
  ],
} as const)

export const purchaseOrderExecution = defineReport({
  key: 'purchase_order_execution', route: 'pur_ord_execution', group: 'Operation',
  label: 'Purchase order execution',
  blurb: 'How much of each order has actually arrived.',
  filters: [rf.project(), rf.supplier(), rf.period('Ordered between', false)],
  columns: [
    rc.doc(), rc.date('documentDate', 'Ordered'), rc.text('supplierName', 'Supplier', 220),
    rc.text('itemName', 'Material'), rc.qty('orderedQty', 'Ordered'),
    rc.qty('receivedQty', 'Received'), rc.qty('openQty', 'Still open'),
    rc.money('openValue', 'Open value'),
  ],
} as const)

export const subcontractExecution = defineReport({
  key: 'subcontract_execution', route: 'sub_ord_exec', group: 'Operation',
  label: 'Subcontract order execution',
  blurb: 'Progress recorded against each subcontract order.',
  filters: [rf.project(), { key: 'supplierId', label: 'Subcontractor', control: 'lookup', lookup: { entity: 'subcontractor' } }],
  columns: [
    rc.doc(), rc.text('supplierName', 'Subcontractor', 220), rc.text('itemName', 'Work'),
    rc.qty('orderedQty', 'Ordered'), rc.qty('doneQty', 'Done'), rc.qty('openQty', 'Remaining'),
    rc.money('openValue', 'Value remaining'),
  ],
} as const)

export const materialProgress = defineReport({
  key: 'material_progress', route: 'material_progress', group: 'Operation',
  label: 'Material progress tracker',
  blurb: 'Estimated, requested, ordered, received and issued — for every material on a project.',
  filters: [rf.project(true), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.qty('estimatedQty', 'Estimated'),
    rc.qty('requestedQty', 'Requested'), rc.qty('orderedQty', 'Ordered'),
    rc.qty('receivedQty', 'Received'), rc.qty('issuedQty', 'Issued to site'),
  ],
} as const)

export const materialIssuedToSubcontractor = defineReport({
  key: 'material_issued_subcontractor', route: 'mat_issue_sco', group: 'Operation',
  label: 'Material issued to subcontractor',
  blurb: 'What has been handed to each subcontractor, for recovery against their bill.',
  filters: [rf.project(), { key: 'supplierId', label: 'Subcontractor', control: 'lookup', lookup: { entity: 'subcontractor' } }],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('supplierName', 'Subcontractor', 220),
    rc.text('itemName', 'Material'), rc.qty('quantity', 'Issued'), rc.money('value', 'Value'),
  ],
} as const)

export const deliveryChallanStatus = defineReport({
  key: 'delivery_challan_status', route: 'del_cln_status', group: 'Operation',
  label: 'Delivery challan status',
  blurb: 'Challans raised, and whether the material has been acknowledged at site.',
  filters: [rf.project(), rf.period('Challan date', false), rf.status()],
  columns: [
    rc.doc(), rc.date('documentDate', 'Date'), rc.text('projectName', 'Project'),
    rc.status(), rc.qty('quantity', 'Quantity'), rc.money('value', 'Value'),
  ],
} as const)

export const purchaseSavings = defineReport({
  key: 'purchase_savings', route: 'purchase_savings', group: 'Operation',
  label: 'Purchase savings',
  blurb: 'Ordered rate against estimated rate — where buying beat the estimate.',
  // The dashboard ranks purchase executives by savings; without the same
  // dimension here the ranking could not be explained line by line, which is
  // the whole of "you can track their performance" (docs/video-notes/07 §2a).
  filters: [
    rf.period('Ordered between'), rf.project(), rf.supplier(),
    { key: 'purchaseExecutiveId', label: 'Purchase executive', control: 'lookup', lookup: { entity: 'employee' } },
  ],
  columns: [
    rc.doc(), rc.text('purchaseExecutiveName', 'Purchase executive', 200),
    rc.text('supplierName', 'Supplier', 220), rc.text('itemName', 'Material'),
    rc.qty('quantity', 'Qty'), { key: 'estimatedRate', label: 'Estimated rate', type: 'rate', width: 140, align: 'right' },
    { key: 'orderedRate', label: 'Ordered rate', type: 'rate', width: 140, align: 'right' },
    rc.signed('saving', 'Saving'),
  ],
  drill: { by: 'document', key: 'id', label: 'The order behind this' },} as const)

export const purchasePriceHistory = defineReport({
  key: 'purchase_price_history', route: 'purchase_price_history', group: 'Operation',
  label: 'Purchase price records',
  blurb: 'What each material has been bought for, and from whom, over time.',
  filters: [{ key: 'itemId', label: 'Material', control: 'lookup', lookup: { entity: 'item' } }, rf.supplier(), rf.period('Period', false)],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('supplierName', 'Supplier', 220),
    rc.text('itemName', 'Material'), rc.qty('quantity', 'Qty'),
    { key: 'rate', label: 'Rate', type: 'rate', width: 140, align: 'right' }, rc.money('amount', 'Amount'),
  ],
  drill: { by: 'document', key: 'id', label: 'The order behind this' },} as const)

export const materialPurchaseForecast = defineReport({
  key: 'material_purchase_forecast', route: 'material_purchase_forecast', group: 'Operation',
  label: 'Material purchase forecast',
  blurb: 'What still has to be bought across live projects, so buying can be consolidated.',
  filters: [rf.project(), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.qty('estimatedQty', 'Estimated'),
    rc.qty('orderedQty', 'Ordered'), rc.qty('toBuyQty', 'Still to buy'),
    rc.money('toBuyValue', 'Estimated value'),
  ],
} as const)

export const employeeAdvances = defineReport({
  key: 'employee_advances', route: 'employee_advances_expenses', group: 'Operation',
  label: 'Employee advances and expenses',
  blurb: 'What each person has claimed and what they still hold as an advance.',
  filters: [rf.employee(), rf.period('Period', false), rf.project()],
  columns: [
    rc.text('employeeName', 'Employee', 240), rc.text('teamName', 'Team', 180),
    rc.doc(), rc.date('documentDate', 'Date'),
    rc.money('claimed', 'Claimed'), rc.money('advance', 'Advance'), rc.signed('balance', 'Balance'),
  ],
} as const)

/* ================= Inventory ================= */

export const stockAvailability = defineReport({
  key: 'stock_availability', route: 'stock_availability', group: 'Inventory',
  label: 'Stock availability',
  blurb: 'What is on the shelf right now, by warehouse.',
  filters: [
    rf.mode([['WAREHOUSE', 'Warehouses'], ['PROJECT', 'Projects'], ['ITEM', 'Materials']]),
    rf.warehouse(), rf.project(), rf.category(),
    { key: 'itemId', label: 'Material', control: 'lookup', lookup: { entity: 'item' } },
  ],
  columns: [
    rc.text('itemName', 'Material'), rc.text('warehouseName', 'Held at', 200),
    rc.qty('inQty', 'Received', true), rc.qty('outQty', 'Issued', true), rc.qty('onHand', 'On hand', true),
    // docs/24 §3.8 — where in the store the stock on hand sits.
    rc.text('bins', 'Bins', 180),
  ],
  drill: {
    by: 'item', key: 'itemId',
    docTypes: ['goods_receipt', 'material_transfer', 'stock_adjustment', 'warehouse_transfer', 'delivery_challan'],
    label: 'What moved this stock',
  },} as const)

export const stockPurchasePlanner = defineReport({
  key: 'stock_purchase_planner', route: 'stock_purchase_planner', group: 'Inventory',
  label: 'Stock purchase planner',
  blurb: 'Where stock is short of what open projects will need, or of its own reorder level.',
  filters: [rf.warehouse(), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.qty('onHand', 'On hand'),
    rc.qty('committed', 'Committed'),
    // docs/15 R-8.6 — the level the item master sets, so the buyer can see
    // which of the two reasons put this row in front of them.
    rc.qty('reorderPoint', 'Reorder point'),
    rc.qty('shortfall', 'Shortfall'),
    rc.text('reason', 'Why', 200),
  ],
} as const)

export const stockPrExecution = defineReport({
  key: 'stock_pr_execution', route: 'stock_pr_exec_status', group: 'Inventory',
  label: 'Stock PR execution',
  blurb: 'Stock purchase requests, and how much has been bought against each.',
  filters: [rf.warehouse(), rf.period('Requested between', false)],
  columns: [
    rc.doc(), rc.date('documentDate', 'Raised'), rc.text('itemName', 'Material'),
    rc.qty('requestedQty', 'Requested'), rc.qty('orderedQty', 'Ordered'), rc.qty('pendingQty', 'Open'),
  ],
} as const)

export const materialInwardOutward = defineReport({
  key: 'material_inward_outward', route: 'material_inward_outward', group: 'Inventory',
  label: 'Material inward and outward',
  blurb: 'Every movement in and out of a store, in date order.',
  /*
    'Show by' belongs to the stock reports, which roll balances up by
    warehouse, project or material. This one is a movement register in date
    order — every row is one line of one document — so there is nothing to roll
    up, and the selector was copied in and read by nothing. Item category is
    the reverse: the runner has always applied it, and nobody was offered it.
  */
  filters: [
    rf.warehouse(), rf.period('Period'), rf.category(),
    { key: 'itemId', label: 'Material', control: 'lookup', lookup: { entity: 'item' } },
  ],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('docTypeLabel', 'Type', 190),
    rc.text('itemName', 'Material'), rc.qty('inQty', 'In', true), rc.qty('outQty', 'Out', true),
  ],
} as const)

export const stockValuation = defineReport({
  key: 'stock_valuation', route: 'stock_valuation', group: 'Inventory',
  label: 'Stock valuation',
  blurb: 'What the stock on hand is worth, at the rate it was received.',
  filters: [rf.warehouse(), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.text('warehouseName', 'Warehouse', 200),
    rc.qty('onHand', 'On hand'), { key: 'rate', label: 'Rate', type: 'rate', width: 140, align: 'right' },
    rc.money('value', 'Value'),
  ],
} as const)

export const stockAgeing = defineReport({
  key: 'stock_ageing', route: 'stock_ageing', group: 'Inventory',
  label: 'Stock ageing',
  blurb: 'How long stock has been sitting, so slow-moving material is visible.',
  filters: [rf.warehouse(), rf.category()],
  columns: [
    rc.text('itemName', 'Material'), rc.text('warehouseName', 'Warehouse', 200),
    rc.qty('onHand', 'On hand'), rc.date('lastReceived', 'Last received'),
    { key: 'ageDays', label: 'Age (days)', type: 'text', width: 130, align: 'right' },
  ],
} as const)

/* ================= Tools ================= */

export const toolAvailability = defineReport({
  key: 'tool_availability', route: 'tool_availability', group: 'Tools and Equipment',
  label: 'Tool availability',
  blurb: 'Which tools are in the store, which are out, and who has them.',
  filters: [rf.warehouse(), { key: 'toolCategoryId', label: 'Tool category', control: 'lookup', lookup: { entity: 'tool_category' } }],
  columns: [
    rc.text('itemName', 'Tool'), rc.text('warehouseName', 'Warehouse', 200),
    rc.qty('addedQty', 'In inventory', true), rc.qty('issuedQty', 'Issued out', true),
    rc.qty('availableQty', 'Available', true),
  ],
} as const)

/**
 * What each contractor got done on a project, side by side.
 *
 * The customer runs four contractors on one site at once and asks the same
 * question of all of them daily: is this moving? Subcontract Order Execution
 * cannot answer it, because three of the four have no order
 * (docs/video-notes/06 §3). Billable lines only — nobody measures the painting.
 */
export const contractorProgress = defineReport({
  key: 'contractor_progress', route: 'contractor_progress', group: 'Operation',
  label: 'Work progress by contractor',
  blurb: 'What each contractor has completed on a project, whether or not an order was issued.',
  filters: [
    rf.project(true), rf.period('Progress recorded', false),
    { key: 'supplierId', label: 'Contractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
  ],
  columns: [
    rc.text('supplierName', 'Contractor', 220), rc.text('boqRef', 'BOQ no', 120),
    rc.text('itemName', 'Work'),
    rc.qty('estimatedQty', 'Estimated'), rc.qty('completedQty', 'Completed'),
    rc.pct('completedPct', 'Done %'),
    rc.text('againstOrder', 'Order', 150),
    rc.date('lastEntry', 'Last entry'),
  ],
} as const)

/**
 * How many people turned up, by day and by contractor.
 *
 * Asked for outright and answered by nothing in the flow chart: "our daily
 * labour count is important — we are not giving it like a lump sum, we have to
 * calculate how many people are coming in daily" (docs/video-notes/06 §5).
 */
export const labourHeadcount = defineReport({
  key: 'labour_headcount', route: 'labour_headcount', group: 'Operation',
  label: 'Daily labour headcount',
  blurb: 'How many people each contractor had on site, day by day.',
  filters: [
    rf.project(true), rf.period('Between', false),
    { key: 'supplierId', label: 'Contractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
  ],
  columns: [
    rc.date('documentDate', 'Date'), rc.text('supplierName', 'Contractor', 240),
    rc.doc(), rc.qty('labourCount', 'People'), rc.qty('labourHours', 'Hours'),
  ],
} as const)

/**
 * Everything bought in a period, as a register.
 *
 * Distinct from Purchase price records, which is item-centric and answers
 * "what has this cost before". This one answers "what did we buy last month,
 * from whom, on which order" — the question the purchase team is asked at a
 * review, and the vendor's own description: *"historical information, what PO
 * you bought before and who you bought from"* (docs/video-notes/06 §9).
 */
export const materialPurchaseRecord = defineReport({
  key: 'material_purchase_record', route: 'material_purchase_record', group: 'Operation',
  label: 'Material purchase record',
  blurb: 'Every purchase made in a period, with its order, supplier, rate and value.',
  filters: [rf.period('Ordered between', false), rf.project(), rf.supplier(), rf.category()],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('supplierName', 'Supplier', 220),
    rc.text('itemName', 'Material'), rc.text('projectName', 'Project', 200),
    rc.qty('quantity', 'Qty'), rc.text('unit', 'Unit', 90),
    rc.money('rate', 'Rate'), rc.money('amount', 'Value'),
  ],
} as const)

/* ================= Integration ================= */

export const integrationStatus = defineReport({
  key: 'integration_status', route: 'tally_sync_status', group: 'Integration',
  label: 'Accounting integration status',
  blurb: 'Which documents have reached the accounting system and which have not.',
  filters: [rf.period('Document date', false), { key: 'docType', label: 'Document type', control: 'select', options: [] }, rf.status()],
  columns: [
    rc.doc(), rc.text('docTypeLabel', 'Type', 190), rc.date('documentDate', 'Date'),
    rc.status(), rc.text('integrationStatus', 'Sync status', 190),
    rc.text('integrationRef', 'Reference in the books', 190),
    rc.money('total', 'Value'),
  ],
} as const)

export const REPORT_DEFS = [
  projectFinancials, completedProfitability, approvalTracker,
  profitAnalysis, costVariance, regulariseBoq, billingReconciliation, projectExpenses,
  materialPendingRequisition, labourPendingSco, materialRequestExecution,
  purchaseOrderExecution, subcontractExecution, materialProgress,
  materialIssuedToSubcontractor, contractorProgress, labourHeadcount,
  deliveryChallanStatus, purchaseSavings,
  purchasePriceHistory, materialPurchaseRecord, materialPurchaseForecast, employeeAdvances,
  stockAvailability, stockPurchasePlanner, stockPrExecution,
  materialInwardOutward, stockValuation, stockAgeing,
  // docs/24 — the supply chain after the purchase order.
  materialRequirementPlan, abcAnalysis, vendorScorecard, poExpediting, materialReconciliation, stockLedger,
  stockAuditStatus,
  toolAvailability, integrationStatus,
] as const satisfies readonly ReportDef[]

const byKey = new Map<string, ReportDef>(REPORT_DEFS.map((d) => [d.key, d]))
const byRoute = new Map<string, ReportDef>(REPORT_DEFS.map((d) => [d.route, d]))
export const getReportDef = (key: string): ReportDef | undefined => byKey.get(key)
export const getReportDefByRoute = (route: string): ReportDef | undefined => byRoute.get(route)
